import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Page } from '@shared/ui/containers/Page';
import { Card } from '@shared/ui/containers/Card';
import { Section } from '@shared/ui/containers/Section';
import { Skeleton } from '@shared/ui/feedback/Skeleton';
import { ErrorState } from '@shared/ui/feedback/ErrorState';
import { EmptyState } from '@shared/ui/feedback/EmptyState';
import { formatMoney, formatDateTime } from '@shared/lib/format';
import { fetchOrder } from './api';

export function OrderDetail(): JSX.Element {
  const { orderId } = useParams<{ orderId: string }>();
  const orderQuery = useQuery({
    queryKey: ['order', orderId],
    queryFn: () => fetchOrder(orderId ?? ''),
    enabled: !!orderId,
  });

  if (orderQuery.isLoading) return <Page><Skeleton className="h-9 w-1/2" /><div className="mt-6 flex flex-col gap-3"><Skeleton className="h-16" /><Skeleton className="h-32" /></div></Page>;
  if (orderQuery.isError) return <Page><ErrorState onRetry={() => orderQuery.refetch()} /></Page>;
  const order = orderQuery.data;
  if (!order) return <Page><EmptyState title="Order not found." /></Page>;

  return (
    <Page>
      <header>
        <span className="eyebrow text-ink-muted">{order.reference}</span>
        <h1 className="font-display text-display-xl text-ink leading-tight mt-1">{formatMoney(order.total, { currency: order.currency })}</h1>
        <p className="text-caption text-ink-muted mt-1">{formatDateTime(order.placed_at)} · {order.status}</p>
      </header>

      <Section title="Items">
        <div className="flex flex-col gap-3">
          {order.items.map((item, i) => (
            <Card key={`${item.product_id}-${i}`} noGrain className="p-3">
              <div className="flex items-center gap-3">
                <div className="h-14 w-14 rounded-md overflow-hidden bg-sunken hairline flex-shrink-0">
                  {item.thumbnail_url ? <img src={item.thumbnail_url} alt="" className="h-full w-full object-cover" /> : null}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-body text-ink truncate font-semibold">{item.name}</p>
                  <p className="text-caption text-ink-muted">{item.quantity} × {formatMoney(item.unit_price, { currency: order.currency })}</p>
                </div>
                <span className="text-body tabular text-ink">{formatMoney(item.line_total, { currency: order.currency })}</span>
              </div>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="Totals">
        <Card>
          <Row label="Subtotal" value={formatMoney(order.subtotal, { currency: order.currency })} />
          {order.shipping !== undefined ? <Row label="Shipping" value={formatMoney(order.shipping, { currency: order.currency })} /> : null}
          {order.tax !== undefined ? <Row label="Tax" value={formatMoney(order.tax, { currency: order.currency })} /> : null}
          <div className="hairline-t mt-2 pt-2">
            <Row label="Total" value={formatMoney(order.total, { currency: order.currency })} bold />
          </div>
        </Card>
      </Section>

      {order.shipping_address ? (
        <Section title="Shipping">
          <Card>
            <p className="text-body text-ink whitespace-pre-line">
              {[order.shipping_address.line1, order.shipping_address.city, order.shipping_address.postal_code, order.shipping_address.country].filter(Boolean).join('\n')}
            </p>
          </Card>
        </Section>
      ) : null}

      {order.payment_method ? (
        <Section title="Payment">
          <Card><p className="text-body text-ink">{order.payment_method}</p></Card>
        </Section>
      ) : null}
    </Page>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }): JSX.Element {
  return (
    <div className="flex justify-between items-baseline py-1">
      <span className={bold ? 'text-body font-semibold text-ink' : 'text-body-sm text-ink-secondary'}>{label}</span>
      <span className={`tabular ${bold ? 'font-display text-headline text-ink' : 'text-body text-ink'}`}>{value}</span>
    </div>
  );
}
